import React, { useRef, useState } from 'react';
import { AreaChartOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Dropdown, Tooltip } from 'antd';
import { ButtonProps } from 'antd/es/button';
import dayjs from 'dayjs';
import { pipelineInProgress } from '@utils/pipelines';
import { getUserFullname } from '@utils/transformation/user';
import MoreOutlined from '@assets/icons/more-outlined.svg?react';
import { useAuthorization } from '@hooks/useAuthorization';
import { useWorkspaceNavigate } from '@hooks/useWorkspaceNavigate';
import {
  CANCEL_BUTTON_LABEL,
  DELETE_BUTTON_LABEL,
  DEPLOY_BUTTON_LABEL,
  DUPLICATE_BUTTON_LABEL,
  EDIT_MENU_LABEL,
  OBSERVABILITY_DASHBOARD_TOOLTIP,
  SHARE_BUTTON_LABEL,
  TRY_AGAIN_BUTTON_LABEL,
  UNDEPLOY_BUTTON_LABEL,
} from '@constants/details-header';
import {
  ACTIVATE_PIPELINE_BUTTON_LABEL,
  DEBUG_BUTTON_LABEL,
  DEPLOYMENT_FAILED_LABEL,
  PipelineDesiredStatusCodes,
  PipelineOutputType,
  PipelineStatusCodes,
  PROMPT_EXPLORER_BUTTON_LABEL,
  RENAME_BUTTON_LABEL,
  UNDEPLOYMENT_FAILED_LABEL,
} from '@constants/pipelines';
import {
  IPipeline,
  PermissionAction,
  PermissionAsset,
  PipelineServiceLevel,
  WithPermissions,
} from '@redux/types/types';
import Authorized from '@components/common/Authorized/Authorized';
import DetailsHeader from '@components/detailsHeader/detailsHeader';
import PipelineConfirmationModal from '@components/pipelines/pipelineConfirmationModal/PipelineConfirmationModal';
import PipelineStatusTag from '@components/pipelineStatusTag/PipelineStatusTag';
import useDebugTunnel from '@modules/DebugTunnel/hooks/useDebugTunnel';
import { LimitCategory } from '@modules/Upgrade/consts/pricing';
import useFreeLimits from '@modules/Upgrade/hooks/useFreeLimits';
import styles from './pipelineDetailsHeader.module.scss';

enum MenuActions {
  Duplicate = 'DUPLICATE',
  Delete = 'DELETE',
  PromptExplorer = 'PROMPT_EXPLORER',
  RenamePipeline = 'RENAME',
  Undeploy = 'UNDEPLOY',
  StartDebugTunnel = 'START_DEBUG_TUNNEL',
}

interface IPipelineDetailsHeader {
  pipeline: IPipeline;
  pipelineName: string;
  deployPipelineHandler: (name: string) => void;
  undeployPipelineHandler: (name: string) => void;
  deletePipelineHandler: (name: string) => void;
  goToPipelinesPageHandler: () => void;
  fetchPipelineHandler: (name: string) => void;
  goToStudio: () => void;
  duplicatePipeline: (name: string) => void;
  onPromptExplorerMenuClick: () => void;
  setShowRenamePipelineModal: (show: boolean) => void;
  onSharePipelineHandler: () => void;
  onActivatePipelineHandler: (name: string) => void;
  pipelineStatusString: string;
  serviceLevel?: React.ReactNode;
}

const PipelineDetailsHeader = (props: IPipelineDetailsHeader) => {
  const workspaceNavigate = useWorkspaceNavigate();
  const {
    pipeline,
    pipelineName,
    deployPipelineHandler,
    undeployPipelineHandler,
    deletePipelineHandler,
    goToPipelinesPageHandler,
    fetchPipelineHandler,
    goToStudio,
    duplicatePipeline,
    onPromptExplorerMenuClick,
    setShowRenamePipelineModal,
    onSharePipelineHandler,
    onActivatePipelineHandler,
    pipelineStatusString,
    serviceLevel,
  } = props;

  const editPipelineNameButtonRef = useRef<HTMLButtonElement>(null);
  const observabilityButtonRef = useRef<HTMLButtonElement>(null);
  const { isLimitedUser, isLimitExceed } = useFreeLimits();
  const { hasPermission } = useAuthorization();

  const writePipelinePermissions = {
    [PermissionAsset.PIPELINES]: PermissionAction.WRITE,
  };
  const writeSharedPrototypePermissions = {
    [PermissionAsset.SHARED_PROTOTYPES]: PermissionAction.WRITE,
  };
  const readPipelinePermissions = {
    [PermissionAsset.PIPELINES]: PermissionAction.READ,
  };

  const [openPipelineConfirmationModal, setOpenPipelineConfirmationModal] = useState(false);
  const [undeployingPipeline, setUndeployingPipeline] = useState(false);

  const { startDebugTunnel } = useDebugTunnel();

  const onUndeployPipeline = async () => {
    setUndeployingPipeline(true);
    setOpenPipelineConfirmationModal(true);
  };

  const onDeployPipeline = async () => {
    await deployPipelineHandler(pipelineName!);
    fetchPipelineHandler(pipelineName);
  };

  const onSharePipeline = async () => {
    await onSharePipelineHandler();
  };

  const onActivatePipeline = async () => {
    await onActivatePipelineHandler(pipelineName!);
    fetchPipelineHandler(pipelineName);
  };

  const handleUndeployRun = async () => {
    await undeployPipelineHandler(pipelineName!);
    fetchPipelineHandler(pipelineName);
    setOpenPipelineConfirmationModal(false);
    setUndeployingPipeline(false);
  };

  const handleDeleteRun = async () => {
    await deletePipelineHandler(pipelineName!);
    goToPipelinesPageHandler();
  };

  const deployButton = () => {
    if (pipeline.status === PipelineStatusCodes.ACTIVATING) return null;

    let buttonProps: WithPermissions<{ label: string; action: () => Promise<void> } & ButtonProps> =
      {
        label: DEPLOY_BUTTON_LABEL,
        action: onDeployPipeline,
        loading: pipelineInProgress(pipeline.status),
        danger: false,
        icon: null,
        disabled: isLimitedUser && isLimitExceed(LimitCategory.CREDITS),
        permissions: writePipelinePermissions,
      };

    if (pipeline.desired_status === PipelineDesiredStatusCodes.DEPLOYED) {
      buttonProps = {
        ...buttonProps,
        label: SHARE_BUTTON_LABEL,
        action: onSharePipeline,
        icon: null,
        disabled: false,
        permissions: writeSharedPrototypePermissions,
      };
    }

    if (pipeline.status === PipelineStatusCodes.IDLE) {
      buttonProps = {
        ...buttonProps,
        label: ACTIVATE_PIPELINE_BUTTON_LABEL,
        action: onActivatePipeline,
        icon: null,
      };
    }

    if (pipelineInProgress(pipeline.status))
      buttonProps = {
        ...buttonProps,
        label: CANCEL_BUTTON_LABEL,
        action:
          pipeline.status === PipelineStatusCodes.DEPLOYMENT_IN_PROGRESS ||
          pipeline.status === PipelineStatusCodes.DEPLOYMENT_SCHEDULED
            ? onUndeployPipeline
            : onDeployPipeline,
        loading: false,
        danger: true,
        icon: null,
        disabled: false,
      };

    if (pipelineStatusString === DEPLOYMENT_FAILED_LABEL)
      buttonProps = {
        label: TRY_AGAIN_BUTTON_LABEL,
        action: onDeployPipeline,
        loading: pipelineInProgress(pipeline.status),
        danger: false,
        icon: <ReloadOutlined />,
      };

    if (pipelineStatusString === UNDEPLOYMENT_FAILED_LABEL)
      buttonProps = {
        label: TRY_AGAIN_BUTTON_LABEL,
        action: onUndeployPipeline,
        loading: pipelineInProgress(pipeline.status),
        danger: false,
        icon: <ReloadOutlined />,
      };

    return (
      <Authorized requiredPermissions={buttonProps.permissions ?? {}} renderAsDisabled>
        <Button onClick={() => buttonProps.action()} {...buttonProps}>
          {buttonProps.label}
        </Button>
      </Authorized>
    );
  };

  const handleMoreActionClick = (key: string) => {
    if (key === MenuActions.Duplicate) duplicatePipeline(pipelineName);
    if (key === MenuActions.PromptExplorer) onPromptExplorerMenuClick();
    if (key === MenuActions.RenamePipeline) setShowRenamePipelineModal(true);
    if (key === MenuActions.Undeploy) onUndeployPipeline();
    if (key === MenuActions.Delete) setOpenPipelineConfirmationModal(true);
    if (key === MenuActions.StartDebugTunnel)
      startDebugTunnel({ pipeline_id: pipeline.pipeline_id, name: pipelineName });
  };

  const actionMenuItems = [
    {
      label: RENAME_BUTTON_LABEL,
      key: MenuActions.RenamePipeline,
      permissions: writePipelinePermissions,
    },
    {
      label: DUPLICATE_BUTTON_LABEL,
      key: MenuActions.Duplicate,
      permissions: writePipelinePermissions,
    },
    ...(pipeline.status === PipelineStatusCodes.DEPLOYED &&
    pipeline.service_level !== PipelineServiceLevel.PRODUCTION
      ? [
          {
            label: DEBUG_BUTTON_LABEL,
            key: MenuActions.StartDebugTunnel,
            permissions: writePipelinePermissions,
          },
        ]
      : []),
    ...(pipeline.status === PipelineStatusCodes.DEPLOYED && pipeline.supports_prompt
      ? [
          {
            label: PROMPT_EXPLORER_BUTTON_LABEL,
            key: MenuActions.PromptExplorer,
          },
        ]
      : []),
    ...(pipeline.desired_status === PipelineDesiredStatusCodes.DEPLOYED
      ? [
          {
            label: UNDEPLOY_BUTTON_LABEL,
            key: MenuActions.Undeploy,
            permissions: writePipelinePermissions,
          },
        ]
      : []),
    {
      label: DELETE_BUTTON_LABEL,
      key: MenuActions.Delete,
      danger: true,
      permissions: writePipelinePermissions,
    },
  ];

  const renderEditButton = () => {
    return (
      <Button
        onClick={() => {
          if (goToStudio) goToStudio();
        }}
      >
        {EDIT_MENU_LABEL}
      </Button>
    );
  };

  const renderActions = () => {
    const authorizedActionsMenuItems = actionMenuItems.filter((item) =>
      hasPermission(item.permissions ?? {}),
    );

    return (
      <>
        {renderEditButton()}
        {deployButton()}
        {pipeline.output_type === PipelineOutputType.GENERATIVE && (
          <Authorized requiredPermissions={readPipelinePermissions}>
            <Tooltip title={OBSERVABILITY_DASHBOARD_TOOLTIP}>
              <Button
                ref={observabilityButtonRef}
                icon={<AreaChartOutlined />}
                onClick={() => {
                  workspaceNavigate(`/groundedness/${pipelineName}`);
                }}
                className={styles.headerActions_button}
              />
            </Tooltip>
          </Authorized>
        )}
        {!!authorizedActionsMenuItems.length && (
          <Dropdown
            menu={{
              items: authorizedActionsMenuItems,
              onClick: ({ key }) => handleMoreActionClick(key),
            }}
            placement="bottomRight"
            trigger={['click']}
          >
            <Button
              ref={editPipelineNameButtonRef}
              className={styles.moreActions_button}
              icon={<MoreOutlined />}
            />
          </Dropdown>
        )}
      </>
    );
  };

  return (
    <>
      <DetailsHeader
        title={pipeline.name}
        subtitle={dayjs(pipeline.created_at).format('DD/MM/YYYY HH:mm')}
        createdBy={getUserFullname(pipeline.created_by)}
        actions={renderActions()}
        status={<PipelineStatusTag pipeline={pipeline} />}
        serviceLevel={serviceLevel}
      />
      <PipelineConfirmationModal
        open={openPipelineConfirmationModal}
        okText={undeployingPipeline ? UNDEPLOY_BUTTON_LABEL : DELETE_BUTTON_LABEL}
        onOk={async () => {
          if (undeployingPipeline) handleUndeployRun();
          else handleDeleteRun();
        }}
        cancelText={CANCEL_BUTTON_LABEL}
        onCancel={() => {
          setOpenPipelineConfirmationModal(false);
          setUndeployingPipeline(false);
        }}
        pipelines={openPipelineConfirmationModal ? [pipeline] : []}
        undeployingPipeline={undeployingPipeline}
      />
    </>
  );
};

export default PipelineDetailsHeader;
